// ===========================================================================
// DEBUG PANEL: never shown unless the page is loaded with ?reportDebug.
// Jumps straight to the mission report (screen 7) for any crew and any ending,
// so every text in the data can be read on screen without building a team.
// Self-activating, js/index.js picks every *Debug.js up on its own, and no
// production code references it.
//
// An ending is either one of the crew's unexpected events, or the wrong team
// message. "Previous" and "Next" walk through every crew and ending in order.
//
// The report is fed a made up team: the crew's requirement holders first, then
// other members up to the crew size. For an unexpected event, that event's
// aptitude gets the highest score so the real picking code lands on it. For
// the wrong team message, no requirement is checked.
//
// The checks at the bottom go through the data for things worth a second look,
// like a missing text or an event that names a different aptitude.
// ===========================================================================

import $ from "jquery";

const WRONG_TEAM = "wrong_team";

// Words to look for in an event text, to catch one filed under the wrong aptitude.
const APTITUDE_WORDS = {
  aptitude_1: ["strateg"],
  aptitude_2: ["level-headed", "level headed"],
  aptitude_3: ["curio"],
  aptitude_4: ["analy"],
  aptitude_5: ["resourceful"],
  aptitude_6: ["leader"],
};

function getContent(app) {
  return app.data.content;
}

// Labels and descriptions are shared by every crew, only the targets differ.
function getAptitudeLabel(app, aptitudeId) {
  const aptitude = getContent(app).aptitudes[aptitudeId];
  return aptitude ? aptitude.label : aptitudeId;
}

// Every crew and ending in order, what "Previous" and "Next" walk through.
function getCombinations(app) {
  const combinations = [];

  Object.keys(getContent(app).team_role).forEach((crewId) => {
    const events = getContent(app).team_role[crewId].unexpected_events || [];
    events.forEach((event) => combinations.push({crewId, ending: event.aptitude}));
    combinations.push({crewId, ending: WRONG_TEAM});
  });

  return combinations;
}

// The crew's requirement holders first, so their cards show up, then whoever
// else is in the list until the crew is full.
function buildTeam(app, crewId) {
  const crewData = getContent(app).team_role[crewId];
  const members = getContent(app).team_members.members_list;
  const requirementIds = crewData.requirements.map((requirement) => requirement.id);

  const holders = Object.keys(members).filter((memberId) => {
    return (members[memberId].requirements || []).some((id) => requirementIds.includes(id));
  });
  const others = Object.keys(members).filter((memberId) => !holders.includes(memberId));

  return holders.concat(others).slice(0, crewData.number_members);
}

function showReport(app, crewId, ending) {
  const crewData = getContent(app).team_role[crewId];
  const isWrongTeam = ending === WRONG_TEAM;

  const render = () => {
    const team = buildTeam(app, crewId);

    // Only the ending's aptitude scores, so it is the one picked.
    const aptitudeSums = isWrongTeam ? {} : {[ending]: 1000};

    // Full gauges never miss their target, the wrong team comes from the requirements alone.
    const topAptitudeIds = isWrongTeam ? ["aptitude_1", "aptitude_2", "aptitude_3"] : [ending];
    const teamTopAptitudes = topAptitudeIds.map((id) => ({id, level: 100}));

    const requirementCounts = {};
    if (!isWrongTeam) crewData.requirements.forEach((requirement) => requirementCounts[requirement.id] = 1);

    app.teamReport.renderTeamReport(team, teamTopAptitudes, requirementCounts, aptitudeSums);
  };

  app.selectCrewType(crewId);

  if (parseInt(app.currentScreen) === 7) render();
  else app.goToScreen(7, render, true);
}

// Looks through the data for anything worth a second look.
function runChecks(app) {
  const content = getContent(app);
  const members = content.team_members.members_list;
  const warnings = [];

  Object.keys(content.team_role).forEach((crewId) => {
    const crewData = content.team_role[crewId];
    const warn = (text) => warnings.push(`${crewId}: ${text}`);

    const goalIds = Object.keys(crewData.aptitude_targets || {});
    if (goalIds.length !== 2) warn(`${goalIds.length} aptitude targets, expected 2`);

    crewData.requirements.forEach((requirement) => {
      const holders = Object.keys(members).filter((memberId) => (members[memberId].requirements || []).includes(requirement.id));
      if (!holders.length) warn(`no member checks "${requirement.label}"`);
    });

    if ((crewData.completed_tasks || []).length !== 2) warn(`${(crewData.completed_tasks || []).length} other tasks, expected 2`);
    if ((crewData.timeline || []).length !== 4) warn(`${(crewData.timeline || []).length} timeline steps, expected 4`);
    if (!crewData.wrong_team_message) warn("no wrong team message");

    const events = crewData.unexpected_events || [];
    if (events.length !== 6) warn(`${events.length} unexpected events, expected 6`);

    events.forEach((event) => {
      const label = getAptitudeLabel(app, event.aptitude);
      const text = (event.text.en || "").toLowerCase();

      if (!text) warn(`"${label}" event has no English text`);

      if (goalIds.includes(event.aptitude)) warn(`"${label}" has an event but is also a goal`);

      const namesOwn = (APTITUDE_WORDS[event.aptitude] || []).some((word) => text.includes(word));
      const namesOther = Object.keys(APTITUDE_WORDS)
        .filter((id) => id !== event.aptitude && APTITUDE_WORDS[id].some((word) => text.includes(word)))
        .map((id) => getAptitudeLabel(app, id));

      if (!namesOwn && namesOther.length) warn(`"${label}" event talks about ${namesOther.join(", ")}`);
    });
  });

  return warnings;
}

// The page styles every button with the display font size and leaves its color
// to the browser, so these say everything themselves.
function makeButton(label, onClick) {
  const button = document.createElement("button");

  button.textContent = label;
  button.style.cssText =
    "flex:1;margin:0;padding:6px;border:0;border-radius:4px;" +
    "font:bold 12px/1.4 monospace;color:#000;background:#3EF0FF;cursor:pointer";
  button.addEventListener("click", onClick);

  return button;
}

function makeSelect() {
  const select = document.createElement("select");
  select.style.cssText = "display:block;width:100%;margin:4px 0 0;padding:4px;font:12px/1.4 monospace";
  return select;
}

function makeHeading(text) {
  const heading = document.createElement("div");
  heading.style.cssText = "margin:10px 0 4px;font-weight:bold;color:#FF3EA5";
  heading.textContent = text;
  return heading;
}

function makeRow(...children) {
  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:6px;margin:6px 0 0";
  row.append(...children);
  return row;
}

function buildPanel(app) {
  const content = getContent(app);
  const combinations = getCombinations(app);
  let index = 0;

  const panel = document.createElement("div");
  panel.style.cssText =
    "position:fixed;top:12px;right:12px;z-index:99999;background:rgba(0,0,0,.85);" +
    "color:#fff;font:12px/1.4 monospace;padding:12px 14px;border-radius:8px;width:360px;" +
    "max-height:calc(100vh - 24px);overflow:auto";

  const crewSelect = makeSelect();
  Object.keys(content.team_role).forEach((crewId) => {
    crewSelect.add(new Option(content.team_role[crewId].title.en, crewId));
  });

  const endingSelect = makeSelect();

  const readout = document.createElement("pre");
  readout.style.cssText = "margin:10px 0 0;white-space:pre-wrap;color:#3EF0FF;font-size:11px";

  const fillEndings = () => {
    const crewData = content.team_role[crewSelect.value];
    endingSelect.innerHTML = "";

    (crewData.unexpected_events || []).forEach((event) => {
      endingSelect.add(new Option(`Event: ${getAptitudeLabel(app, event.aptitude)}`, event.aptitude));
    });
    endingSelect.add(new Option("Wrong team message", WRONG_TEAM));
  };

  const show = () => {
    const crewId = crewSelect.value;
    const ending = endingSelect.value;

    index = combinations.findIndex((combination) => combination.crewId === crewId && combination.ending === ending);
    showReport(app, crewId, ending);

    readout.textContent =
      `${index + 1} / ${combinations.length}\n` +
      `crew: ${crewId}\n` +
      `ending: ${ending === WRONG_TEAM ? "wrong team message" : getAptitudeLabel(app, ending) + " event"}\n` +
      `team: ${buildTeam(app, crewId).map((memberId) => content.team_members.members_list[memberId].name).join(", ")}`;
  };

  const showCombination = (newIndex) => {
    const combination = combinations[(newIndex + combinations.length) % combinations.length];
    crewSelect.value = combination.crewId;
    fillEndings();
    endingSelect.value = combination.ending;
    show();
  };

  crewSelect.addEventListener("change", () => {
    fillEndings();
    show();
  });
  endingSelect.addEventListener("change", show);

  panel.append(makeHeading("Mission report"), crewSelect, endingSelect);
  panel.append(makeRow(
    makeButton("Previous", () => showCombination(index - 1)),
    makeButton("Show", show),
    makeButton("Next", () => showCombination(index + 1))
  ));
  panel.append(makeRow(
    makeButton("English", () => app.i18n.set("en")),
    makeButton("French", () => app.i18n.set("fr"))
  ));
  panel.append(readout);

  const warnings = runChecks(app);
  const checks = document.createElement("pre");
  checks.style.cssText = "margin:4px 0 0;white-space:pre-wrap;font-size:11px;color:" + (warnings.length ? "#FFC83E" : "#7CFF9B");
  checks.textContent = warnings.length ? warnings.map((warning) => `- ${warning}`).join("\n") : "Nothing to report";

  panel.append(makeHeading(`Data checks (${warnings.length})`), checks);

  document.body.appendChild(panel);
  fillEndings();
}

function init() {
  if (!new URLSearchParams(window.location.search).has("reportDebug")) return;

  const tryAttach = () => {
    const app = window.app;
    if (!app || !app.teamReport || !app.data) return setTimeout(tryAttach, 200);

    // Reading the texts takes a while, don't let the kiosk go back to idle meanwhile.
    app.pauseScreenSaver();

    buildPanel(app);
  };

  tryAttach();
}

init();
